import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ClientEndpoints, DeviceAccessTokens, DeviceCommands, Devices, HardwareStatuses, OrganizationEvents, PhotoMetas } from "@app/shared/src/api/api.gen";
import { IUpdatePhotoCommand, UpdatePhotoCommand } from "@app/shared/src/models";
import { WebRtcPlayer } from "@app/shared/src/components/WebRtcPlayer";
import {
    Btn,
    Card,
    CardHead,
    ChartLegend,
    FRow,
    FVal,
    LineAreaChart,
    Note,
    PlayIcon,
    Spinner,
    Tag,
    Table,
    Td,
    Th,
    formatAgo,
    formatDateTime,
    formatUptime,
    timeZoneLabel,
    useToast,
} from "@app/shared/src/components_v2";
import { isValidCellSignal } from "@app/shared/src/helpers/hardwareStatusHelpers";
import { BackLink } from "../../helpers/backLink";
import { cabCardUrl } from "../../helpers/cabLinks";
import { EventTag, eventSpeedMph } from "../../helpers/eventDisplay";
import { findRelayEndpoint } from "../../helpers/notificationSettings";
import { RELAY_HEALTH_UNREADABLE, relayHealth } from "../../helpers/relayHealth";
import { useLoad } from "../../hooks/useLoad";
import { usePoll } from "../../hooks/usePoll";
import { useTraffic } from "../../hooks/useTraffic";
import { useZoneNames } from "../../hooks/useZoneNames";
import { useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";
import { DeviceMediaSection } from "../sections/DeviceMediaSection";
import { DeviceStatusTag } from "../shared/DeviceStatusTag";
import { StatusDot } from "../shared/StatusDot";
import { UpgradePackagesDialog } from "../shared/UpgradePackagesDialog";

/**
 * One truck at a glance: the last frame it uploaded, its traffic and events, its health — with
 * settings and the cab view one click away. The persistent configuration lives on the settings
 * page; this one answers "what is this unit seeing and is it well?"
 */

const DAYS = 14;
const REFRESH_MS = 60_000;
/** Heartbeat age past which a unit is not going to answer a queued request any time soon. */
const ONLINE_MS = 15 * 60 * 1000;
const PHOTO_POLL_MS = 15_000;
/** A frame older than this says nothing about what the truck is looking at now. */
const STALE_PHOTO_MS = 4 * 60 * 60 * 1000;
/** Stop waiting on a unit that took the request but never uploaded. */
const PHOTO_WAIT_MS = 10 * 60 * 1000;

export const DeviceOverviewPage: React.FC = () => {
    const { deviceId } = useParams<{ deviceId: string }>();
    const location = useLocation() as { state?: BackLink };
    const { org } = useOrg();
    // Org-wide, not group-scoped: a link here can come from search, a bookmark or an event on a
    // truck the current filter hides, and "that unit isn't in this organization" would be a lie.
    const { allDevices, reload } = useFleet();
    const zoneNames = useZoneNames();
    const navigate = useNavigate();
    const toast = useToast();
    const [live, setLive] = useState(false);
    const [startingLive, setStartingLive] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const device = allDevices.find((d) => d.id === deviceId);
    const deviceIds = useMemo(() => (deviceId ? [deviceId] : []), [deviceId]);
    const traffic = useTraffic(deviceIds, DAYS);

    // Health is the reason this page is left open on a second monitor, so it keeps itself current.
    const hardware = useLoad(
        () => (deviceId ? HardwareStatuses.getRecentForDevice(deviceId, 1) : Promise.resolve(undefined)),
        [deviceId],
        { pollMs: REFRESH_MS },
    );

    /*
     * Whether the truck's relay is actually closing.
     *
     * The with-status route rather than the plain endpoint list: the delivery-health fields the Health
     * card reads (status, last success, the failure streak, the window rollup) are only populated there.
     * Polled with the rest of the card, because a relay that stopped answering an hour into a shift is
     * exactly the thing someone watching this page needs to see without reloading.
     */
    const relay = useLoad(async () => {
        if (!device?.organizationId || !device?.facilityId) return undefined;
        const endpoints = await ClientEndpoints.getWithStatusByFacilityId(device.organizationId, device.facilityId);
        return findRelayEndpoint(endpoints, device.facilityId);
    }, [device?.organizationId, device?.facilityId], { pollMs: REFRESH_MS });

    // The freshest photo this unit uploaded — what the unit sees when nobody is watching live.
    const lastFrame = useLoad<{ url?: string; when?: Date } | undefined>(async () => {
        if (!deviceId || !org?.id) return undefined;
        const page = await PhotoMetas.search(1, "dateCreated", "desc", org.id, deviceId);
        const meta = page?.data?.[0];
        if (!meta?.id) return undefined;
        const urls = await PhotoMetas.getDownloadUrls([meta.id]);
        return { url: urls?.[0]?.url, when: meta.dateCreated ? new Date(meta.dateCreated) : undefined };
    }, [deviceId, org?.id]);

    const events = useLoad(
        () =>
            org?.id && deviceId
                ? OrganizationEvents.getByOrganization(
                      org.id, 500, "dateCreated", "desc", undefined, deviceId, undefined, undefined, undefined, new Date(Date.now() - DAYS * 86_400_000),
                  )
                : Promise.resolve(undefined),
        [org?.id, deviceId],
        { pollMs: REFRESH_MS },
    );

    const cabToken = useLoad(async () => {
        if (!deviceId) return undefined;
        const tokens = await DeviceAccessTokens.getForDevice(deviceId);
        return tokens?.find((t) => !t.revokedAt && (!t.expiresAt || new Date(t.expiresAt) > new Date()));
    }, [deviceId]);

    // Asks the server to queue a StreamVideo start command; the player then negotiates with the
    // unit directly over WebRTC. Fleet Commander units all run 2.x firmware, so the legacy Kinesis
    // path the dashboard still carries is deliberately not here.
    const startLive = async () => {
        if (!device || startingLive) return;
        setStartingLive(true);
        try {
            const url = await Devices.getDeviceStream(device.id);
            if (!url) throw new Error("No stream URL returned");
            setLive(true);
        } catch (e) {
            console.error("Failed to start live view", e);
            toast("Could not start live view — try again in a moment");
        } finally {
            setStartingLive(false);
        }
    };

    const dayKeys = useMemo(() => {
        const keys: string[] = [];
        for (let i = DAYS - 1; i >= 0; i--) keys.push(new Date(Date.now() - i * 86_400_000).toDateString());
        return keys;
    }, []);
    const dayLabels = dayKeys.map((k) => new Date(k).toLocaleDateString(undefined, { month: "short", day: "numeric" }));
    const eventsPerDay = useMemo(() => {
        const m = new Map(dayKeys.map((k) => [k, 0]));
        for (const e of events.data?.data ?? []) {
            const k = new Date(e.dateCreated ?? 0).toDateString();
            if (m.has(k)) m.set(k, (m.get(k) ?? 0) + 1);
        }
        return m;
    }, [events.data, dayKeys]);
    const trafficPerDay = useMemo(() => {
        const m = new Map<string, number>();
        for (const r of traffic.data ?? []) if (r.periodStart) m.set(new Date(r.periodStart).toDateString(), r.count ?? 0);
        return m;
    }, [traffic.data]);

    const backTo = location.state?.from ?? "/devices";
    const backLabel = location.state?.fromLabel ?? "All devices";

    // Sorted here as well as in the query, so the newest event is on top no matter what order the
    // server answered in.
    const recent = (events.data?.data ?? [])
        .filter((e) => Date.now() - new Date(e.dateCreated ?? 0).getTime() <= 86_400_000)
        .sort((a, b) => new Date(b.dateCreated ?? 0).getTime() - new Date(a.dateCreated ?? 0).getTime());
    const latestHw = hardware.data?.[0];
    // The modem reports strength as a percentage, same as the main dashboard's cell signal card;
    // out-of-range values are the modem saying it doesn't know rather than a reading of zero.
    const signal = latestHw?.signal;
    // A lookup that failed says nothing about the relay, so it must not render as "not configured".
    const relayVerdict = relay.error ? RELAY_HEALTH_UNREADABLE : relayHealth(relay.data);

    /*
     * Getting a frame without being asked.
     *
     * A truck with no photo is the case where you most want to see one, so opening the page asks
     * for it rather than offering a button to ask. The command is queued like any other and the
     * device answers by uploading a photo, which arrives through the same PhotoMetas search this
     * card already reads — so the poll below is what actually surfaces it.
     *
     * Two guards: only while the unit is checking in, because a queued request to a truck that is
     * switched off just sits there; and once per unit per visit, via a ref rather than state, so a
     * re-render or a background refresh can't turn one request into a stream of them.
     */
    const isOnline = device ? Date.now() - new Date(device.lastHeartbeat ?? 0).getTime() <= ONLINE_MS : false;
    const askedFor = useRef<string>();
    /** The frame we had when we asked — how we recognise the new one arriving. */
    const askedAgainst = useRef<number>();
    const givesUpAt = useRef<number>(0);
    const [photoRequested, setPhotoRequested] = useState(false);

    const frameMs = lastFrame.data?.when ? new Date(lastFrame.data.when).getTime() : undefined;
    // No frame at all, or one old enough that it no longer says anything about today's deployment.
    const frameIsStale = frameMs === undefined || Date.now() - frameMs > STALE_PHOTO_MS;

    useEffect(() => {
        if (!deviceId || !isOnline || askedFor.current === deviceId) return;
        // Wait for the lookup to actually settle — firing mid-load would ask every unit for a
        // photo it may already have.
        if (lastFrame.isLoading || !frameIsStale) return;
        askedFor.current = deviceId;
        askedAgainst.current = frameMs;
        givesUpAt.current = Date.now() + PHOTO_WAIT_MS;
        setPhotoRequested(true);
        DeviceCommands.sendCommand(new UpdatePhotoCommand({ deviceId } as unknown as IUpdatePhotoCommand)).catch(() => {
            // Quietly: nobody asked for this, so a failure is not worth interrupting them with.
            // The card keeps showing what it has, which remains true.
            setPhotoRequested(false);
        });
    }, [deviceId, isOnline, lastFrame.isLoading, frameIsStale, frameMs]);

    // Watch for the new frame rather than making someone reload. "New" means a different timestamp
    // from the one we asked against, so this ends correctly whether the unit had an old photo or
    // none at all — and gives up rather than polling a truck that is never going to answer.
    const awaitingFrame = photoRequested && frameMs === askedAgainst.current;
    usePoll(() => {
        if (Date.now() > givesUpAt.current) {
            setPhotoRequested(false);
            return;
        }
        lastFrame.reload();
    }, awaitingFrame ? PHOTO_POLL_MS : undefined);

    // Package selection happens in the dialog; this only has to know the request went through.
    const onUpgradeQueued = () => {
        toast("Update queued — the unit installs it on its next check-in");
        reload();
    };

    if (!device) {
        return allDevices.length === 0 ? (
            <div className="grid place-items-center p-12 text-[var(--muted)]">
                <Spinner />
            </div>
        ) : (
            <Note tone="crit">That unit isn't in this organization.</Note>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Back to the list you came from, filter and all — arriving from "Needs attention"
                and being dropped into the whole fleet loses your place. */}
            <Link to={backTo} state={location.state?.fromState} className="text-[12.5px] font-semibold text-[var(--muted)]">
                ← {backLabel}
            </Link>

            {/* Name carries the health dot; the detail line underneath carries identity and status. */}
            <div className="flex flex-wrap items-end gap-4">
                <div>
                    <h1 className="flex items-center gap-3 text-[28px] font-bold tracking-tight">
                        <StatusDot status={device.status} lastHeartbeat={device.lastHeartbeat} />
                        {device.name}
                    </h1>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2.5 text-[13px] text-[var(--muted)]">
                        <DeviceStatusTag status={device.status} lastHeartbeat={device.lastHeartbeat} />
                        <span>
                            key <span className="font-mono">{device.deviceKey}</span> · firmware {device.firmwareVersion ?? "—"}
                        </span>
                    </div>
                </div>
                <span className="ml-auto flex flex-wrap gap-2.5">
                    <a
                        href={cabToken.data ? cabCardUrl(cabToken.data.token) : undefined}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => {
                            if (!cabToken.data) {
                                e.preventDefault();
                                toast("This truck has no active cab card — issue one under Device settings");
                            }
                        }}
                    >
                        <Btn tone="quiet">Open cab view</Btn>
                    </a>
                    <Btn onClick={() => navigate(`/devices/${device.id}/settings`)}>Device settings</Btn>
                </span>
            </div>

            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardHead
                                title="Last frame"
                                note={
                                    lastFrame.data?.when
                                        ? `${formatAgo(lastFrame.data.when)}${awaitingFrame ? " · asking for a fresh one" : ""}`
                                        : undefined
                                }
                            />
                            <div className="p-5 pt-3">
                                {lastFrame.isLoading ? (
                                    <div className="grid aspect-[4/3] place-items-center rounded-[12px] bg-[var(--surface-2)] text-[var(--muted)]">
                                        <Spinner />
                                    </div>
                                ) : (
                                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[12px] border border-[var(--line-2)] bg-[var(--surface-2)]">
                                        {live ? (
                                            <>
                                                <WebRtcPlayer device={device} className="absolute inset-0 h-full w-full bg-black object-contain" />
                                                <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-lg bg-black/70 px-3 py-1.5 text-[11.5px] font-bold text-white">
                                                    <i className="h-[7px] w-[7px] rounded-full bg-[var(--crit)]" />
                                                    LIVE
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setLive(false)}
                                                    className="absolute right-3 top-3 rounded-lg bg-black/70 px-3 py-1.5 text-[11.5px] font-bold text-white hover:bg-black/85"
                                                >
                                                    Stop
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {lastFrame.data?.url ? (
                                                    <img
                                                        src={lastFrame.data.url}
                                                        alt={`Last frame uploaded by ${device.name}`}
                                                        className="absolute inset-0 h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="absolute inset-0 grid place-items-center gap-2 px-6 text-center text-[12.5px] leading-relaxed text-[var(--muted)]">
                                                        {awaitingFrame ? (
                                                            <>
                                                                <Spinner />
                                                                <span>Asked this unit for a frame — it uploads one on its next check-in.</span>
                                                            </>
                                                        ) : (
                                                            <span>
                                                                No photos uploaded by this unit yet.
                                                                {!isOnline && " It has to be checking in before it can send one."}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label="Start live view"
                                                    disabled={startingLive}
                                                    onClick={startLive}
                                                    className="absolute inset-0 grid place-items-center text-white"
                                                >
                                                    <span className="grid h-[62px] w-[62px] place-items-center rounded-full border-2 border-white/85 bg-black/60 pl-1 opacity-50 transition-opacity hover:opacity-90">
                                                        {startingLive ? <Spinner /> : <PlayIcon size={28} />}
                                                    </span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <CardHead title="Traffic & events" note={`Last ${DAYS} days`} />
                            {traffic.isLoading || events.isLoading ? (
                                <div className="grid place-items-center p-10 text-[var(--muted)]">
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <LineAreaChart
                                        height={250}
                                        labels={dayLabels}
                                        series={[
                                            { key: "veh", label: "Vehicles", color: "var(--s1)", area: true, values: dayKeys.map((k) => trafficPerDay.get(k) ?? 0) },
                                            {
                                                key: "ev",
                                                label: "Events",
                                                color: "var(--s2)",
                                                values: dayKeys.map((k) => eventsPerDay.get(k) ?? 0),
                                                // Its own scale, as on Overview: a handful of alerts against
                                                // thousands of vehicles is a flat line on a shared one.
                                                axis: "right",
                                            },
                                        ]}
                                    />
                                    <ChartLegend
                                        items={[
                                            { label: "Vehicles (left)", color: "var(--s1)" },
                                            { label: "Events (right)", color: "var(--s2)" },
                                        ]}
                                    />
                                </>
                            )}
                        </Card>
                    </div>

                    <Card>
                        <CardHead title="Recent events" note="Last 24 hours">
                            <Link to={`/events?device=${device.id}`}>
                                <Btn tone="quiet">View all</Btn>
                            </Link>
                        </CardHead>
                        {events.isLoading ? (
                            <div className="grid place-items-center p-8 text-[var(--muted)]">
                                <Spinner />
                            </div>
                        ) : recent.length === 0 ? (
                            <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">No events in the last 24 hours.</p>
                        ) : (
                            <Table className="mt-2">
                                    <thead>
                                        <tr>
                                            <Th>Time ({timeZoneLabel()})</Th>
                                            <Th>Event</Th>
                                            <Th>Speed</Th>
                                            <Th className="text-right">Report</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recent.slice(0, 8).map((e) => {
                                            const speed = eventSpeedMph(e);
                                            return (
                                                <tr
                                                    key={e.id}
                                                    className="cursor-pointer hover:bg-[var(--surface-2)]"
                                                    onClick={() =>
                                                        navigate(`/events/${e.id}`, { state: { event: e, from: `/devices/${device.id}` } })
                                                    }
                                                >
                                                    <Td label="Time" className="tabular-nums">{formatDateTime(e.dateCreated)}</Td>
                                                    <Td label="Event">
                                                        <EventTag event={e} zoneName={e.detectionZoneId ? zoneNames.get(e.detectionZoneId) : undefined} />
                                                    </Td>
                                                    <Td label="Speed" className="tabular-nums">{speed !== undefined ? `${speed} mph` : "—"}</Td>
                                                    <Td className="text-right font-semibold text-[var(--accent)]">Open</Td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                            </Table>
                        )}
                    </Card>

                    <DeviceMediaSection device={device} />
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                    <Card>
                        <CardHead title="Health" />
                        <div className="mt-2">
                            <FRow title="Status">
                                <DeviceStatusTag status={device.status} lastHeartbeat={device.lastHeartbeat} />
                            </FRow>
                            <FRow title="Last online" sub={device.lastHeartbeat ? formatDateTime(device.lastHeartbeat) : undefined}>
                                <FVal>{formatAgo(device.lastHeartbeat)}</FVal>
                            </FRow>
                            {/* Third, above the device's own vitals: a unit can be online, current and
                                well while its horn and beacons have been dead for a week, and this is
                                the only row on the page that would say so. */}
                            <FRow title="Relay" sub={relay.isLoading ? "Horn and beacons" : relayVerdict.detail}>
                                {relay.isLoading ? <FVal mu>—</FVal> : <Tag tone={relayVerdict.tone}>{relayVerdict.label}</Tag>}
                            </FRow>
                            <FRow title="Uptime" sub="Since last reboot">
                                <FVal mu>{latestHw ? formatUptime(latestHw.upTime) : "—"}</FVal>
                            </FRow>
                            <FRow
                                title="Firmware"
                                sub={
                                    device.hasPendingDeviceCommands
                                        ? "A command is waiting for the next check-in"
                                        : "Installs on the next check-in"
                                }
                            >
                                <FVal mu>{device.firmwareVersion ?? "—"}</FVal>
                                <Btn tone="quiet" onClick={() => setUpgradeOpen(true)}>
                                    Update
                                </Btn>
                            </FRow>
                            <FRow title="Signal" sub="Cellular strength">
                                <FVal mu>{isValidCellSignal(signal) ? `${signal}%` : "—"}</FVal>
                            </FRow>
                            <FRow title="IP address">
                                <FVal mu mono>
                                    {device.ipv4Address ?? "—"}
                                </FVal>
                            </FRow>
                            <FRow title="Site">
                                <FVal mu>{device.facilityName ?? "—"}</FVal>
                            </FRow>
                        </div>
                    </Card>

                    <Card>
                        <CardHead title="Cab card" />
                        <div className="mt-2">
                            <FRow title="Driver access" sub="Scanning opens this truck only — no login">
                                {cabToken.data ? <Tag tone="safe">Active</Tag> : <Tag tone="mute">No card</Tag>}
                            </FRow>
                        </div>
                        <div className="px-5 pb-5 pt-1">
                            <Link to={`/devices/${device.id}/settings`}>
                                <Btn tone="quiet" className="w-full">
                                    Manage cab card
                                </Btn>
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>

            <UpgradePackagesDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} device={device} onQueued={onUpgradeQueued} />
        </div>
    );
};
